<template>
    <div>
        <hts-router-drawer
            :title="$t('labels.document')"
            size="large"
            @loaded="initData">
            <div
                v-if="checkPermission(layoutPermissions['partner_document_add'])"
                class="mb-3 text-right">
                <hts-form-button
                    color="main"
                    icon-name="hi-plus-circle-icon"
                    icon-position="right"
                    @click="openAddDocument">
                    {{ $t('labels.add') }}
                </hts-form-button>
            </div>
            <hts-data-table
                ref="dataDocument"
                store-name="cooperatorDataTable"
                store-key="CooperatorDocument"
                :use-archive="useArchive"
                :use-restore="useRestore"
                use-selection
                :headers="documentCooperator"
                :server="server"
                :detail-row-auto-height="true" />
            <hts-modal
                :show="showPopupDeleteCooperatorDocument"
                :title="$t('labels.deleteDocument')"
                type="confirm"
                :content="$t('labels.deleteDocumentContent')"
                @hide="closePopupDeleteCooperatorDocument"
                @confirm="() => actionDeleteCooperatorDocument()" />
            <add-document
                v-if="checkPermission(layoutPermissions['partner_document_add'])"
                ref="addDocument"
                :code-partner="cooperatorId"
                @refresh-tables="refreshTables()" />
        </hts-router-drawer>
        <div>
            <router-view @refresh-tables="refreshTables" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import actions from '../-components/actions.vue'
import AddDocument from './document/-components/add-document.vue'
import { $downloadjs } from '#imports'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
const { $formatters } = useNuxtApp()

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  }
})
const emits = defineEmits(['close'])

const cooperatorId = computed(() => useRoute().params.idpartner)

const server = {
  method: 'post',
  url: '/partner/document/file/' + cooperatorId.value
}

const handleEditDocument = (document: any) => {
  useRouter().push({ name: 'trucker-id-partner-idpartner-document-iddocumentpartner-edit', params: { iddocumentpartner: document._id } })
}

const handleDownloadContract = (data: any) => {
  downloadContract(data.cooperator, data._id)
}

const handleDeleteCooperatorDocument = (document: object) => {
  showPopupDeleteCooperatorDocument.value = true
  Object.assign(cooperatorDocumentIsSelectedForDelete, document)
}
const addDocument: any = ref(null)
const openAddDocument = () => {
  if (addDocument.value) {
    addDocument.value.open(typeCooperator.value)
  }
}
const validateDownloadContract = (document: any) => {
  return document.createContract
}

const validateStopEffect = (document: any) => {}
const documentCooperator = [
  {
    field: 'type',
    headerName: $t('labels.documentType'),
    sortable: true,
    valueFormatter: (params: any) => {
      if (params.data.type === 'CONTRACT') {
        return $t('labels.contract')
      }
      if (params.data.type === 'CERTIFICATEBUSINESS') {
        return $t('labels.certificateBusiness')
      }
      if (params.data.type === 'OTHER') {
        return $t('labels.other')
      }
    }
  },
  {
    field: 'contractFor',
    headerName: $t('labels.contractFor'),
    sortable: true,
    valueFormatter: (params: any) => {
      if (params.data.type !== 'CONTRACT') {
        return
      }
      if (params.data.contractFor === 'VENDOR') {
        return $t('labels.vendor')
      }
      if (params.data.contractFor === 'CUSTOMER') {
        return $t('labels.customer')
      }
      if (params.data.contractFor === 'PARTNER') {
        return $t('labels.partner')
      }
    }
  },
  {
    field: 'fileName',
    headerName: $t('labels.name'),
    sortable: true
  },
  {
    field: 'path',
    headerName: $t('labels.link'),
    sortable: true,
    valueFormatter: (params: any) => {
      if (params.data.createContract) {
        return ''
      }
      return $formatters.cdnUrl(params.data)
    },
    onCellClicked: (params: any) => {
      if (params.data.createContract) {
        return ''
      }
      window.open($formatters.cdnUrl(params.data))
    }
  },
  {
    field: 'dueDate',
    headerName: $t('labels.dueDate'),
    valueGetter: (params: any) => {
      if (params.data.dueDate) {
        return $formatters.date(params.data.dueDate)
      }
    }
  },
  {
    field: 'effectiveDate',
    headerName: $t('labels.effectiveDate'),
    valueGetter: (params: any) => {
      if (params.data.effectiveDate) {
        return $formatters.date(params.data.effectiveDate)
      }
    }
  },
  {
    field: 'creditTermsBy',
    headerName: $t('labels.creditTermsBy'),
    valueGetter: (params: any) => {
      if (params?.data?.creditTermsBy) {
        return $t(`labels.${params.data.creditTermsBy}`)
      }
      return ''
    }
  },
  {
    field: 'dayOfTheMonth',
    headerName: $t('labels.dayOfTheMonth'),
    valueGetter: (params: any) => {
      if (params.data.dayOfTheMonth) {
        return $formatters.formatCurrency(params.data.dayOfTheMonth)
      }
    }
  },
  {
    field: 'monthsCreditTerms',
    headerName: $t('labels.monthsCreditTerms'),
    valueGetter: (params: any) => {
      if (params.data.dayOfTheMonth) {
        return $formatters.formatCurrency(params.data.monthsCreditTerms)
      }
    }
  },
  {
    field: 'duration',
    headerName: $t('labels.duration'),
    valueGetter: (params: any) => {
      if (params.data.duration) {
        return $formatters.formatCurrency(params.data.duration)
      }
    }
  },
  {
    field: 'spendingLimit',
    headerName: $t('labels.spendingLimit'),
    valueGetter: (params: any) => {
      if (params.data.spendingLimit) {
        return $formatters.formatCurrency(params.data.spendingLimit)
      }
    }
  },
  {
    field: 'contractStatus',
    headerName: $t('labels.contractStatus'),
    minWidth: 120,
    valueGetter: (params: any) => {
      if (params.data.contractStatus !== undefined && params.data.customerStatus !== null) {
        if (params.data.type !== 'CONTRACT') {
          return
        }
        const effectiveDate = params.data.effectiveDate + ' 00:00:00'
        const dueDate = params.data.dueDate + ' 23:59:59'

        if (params.data.stopEffect) {
          return $t('labels.stopEffect')
        }

        if (params.data.contractStatus === 0) {
          return $t('labels.notApprovedYet')
        }

        if (params.data.contractStatus === -1) {
          return $t('labels.refuse')
        }

        if (params.data.contractStatus === 1) {
          const now = new Date()
          if (effectiveDate && new Date(effectiveDate) > now) {
            return $t('labels.approved')
          }

          if (dueDate && new Date(dueDate) < now) {
            return $t('labels.outOfEffect')
          }

          return $t('labels.effect')
        }
      }
    }
  },
  {
    field: 'salesDirectorName',
    headerName: $t('labels.salesDirector')
  },
  {
    field: 'description',
    headerName: $t('labels.description'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.description === 'undefined') {
        return ''
      }
      return params.data.description
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createDate'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: actions,

    cellRendererParams: {
      storeKey: 'cooperatorDataTable',
      storeName: 'CooperatorDocument',
      items: [
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.partner_document_edit),
          click: handleEditDocument
        },
        {
          icon: 'hi-arrow-down-tray-icon',
          label: $t('labels.downloadContract'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.partner_document_management),
          validate: validateDownloadContract,
          click: handleDownloadContract
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.partner_document_delete),
          click: handleDeleteCooperatorDocument,
          isArchive: true
        }
      ]
    }
  }
]

const dataDocument = ref()

const refreshTables = () => {
  if (dataDocument.value) {
    dataDocument.value.refresh()
  }
}

const visible = ref(false)

const typeCooperator: { [key: string]: any } = ref([])

const cooperatorDocumentIsSelectedForDelete = reactive({
  _id: ''
})
const showPopupDeleteCooperatorDocument = ref(false)

const closePopupDeleteCooperatorDocument = () => {
  showPopupDeleteCooperatorDocument.value = false
  Object.assign(cooperatorDocumentIsSelectedForDelete, {})
}

const actionDeleteCooperatorDocument = () => {
  const api = {
    url: '/partner/document/delete',
    method: 'DELETE'
  }
  $api(api, { body: { ids: [cooperatorDocumentIsSelectedForDelete._id] } })
    .then((data: any) => {
      const resData: any = data.data.value

      if (resData.success) {
        Object.assign(cooperatorDocumentIsSelectedForDelete, {})
        refreshTables()
        showPopupDeleteCooperatorDocument.value = false
        process.client && $toast().success($t('labels.deleteSuccess', { name: $t('labels.partner') }))
      }
    })
    .catch(() => {
      process.client && $toast().error($t('labels.canNotConnectServer'))
    })
}

const downloadContract = (cooperatorId: string, id: string) => {
  const api = {
    url: '/partner/document/cooperatorId/download-contract/ID',
    method: 'get'
  }
  $api(api, { uriParams: { cooperatorId, ID: id }, contentType: 'blob' })
    .then((data: any) => {
      const resData: any = data.data.value
      const fileName = resData.headers.get('filename') || 'Contract.docx'
      const contentType = resData.headers.get('Content-type')
      $downloadjs(resData, fileName, contentType)
    })
    .catch(() => {
      process.client && $toast().error($t('labels.canNotConnectServer'))
    })
}
const loading = ref(false)
const getCooperator = () => {
  const api = $url.partner['get-detail']
  $api(api, { uriParams: { ID: cooperatorId.value } })
    .then((data: any) => {
      const resData: any = data.data.value
      if (resData.success) {
        typeCooperator.value = resData.data.type
      }
    })
    .catch(() => {})
}

const initData = async () => {
  loading.value = true
  await getCooperator()
  loading.value = false
}

const useArchive = { api: $url.partner.archiveDocument, permissions: checkPermission(layoutPermissions.partner_document_archive) }
const useRestore = { api: $url.partner.restoreDocument, permissions: checkPermission(layoutPermissions.partner_document_restore) }
</script>
